import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  serverTimestamp,
  updateDoc,
  type DocumentData,
} from 'firebase/firestore';
import type { EmployeeDesignation, User, UserRole } from '../types';
import { db } from './firebase';
import {
  buildGanttSyncedOutlineAll,
  type GanttGanttOutlineSlice,
  type GanttSyncedOutline,
  applyGanttSyncedOutline,
  type ApplyGanttSyncedOutlineHandlers,
} from './ganttTimelineViewSync';
import type {
  GroupTimelineColorMode,
  GroupTimelineExtraTasksByFamily,
  GroupTimelineFamily,
  GroupTimelineLayoutMode,
  GroupTimelineSortMode,
} from './groupTimeline';
import {
  parseGroupTimelineDetailFilter,
  type GroupTimelineDetailFilter,
} from './groupTimelineFilter';
import { parseAudienceUserIds, releaseMatchesUserAudience } from './releaseAudience';

export const GROUP_TIMELINE_SAVED_VIEWS_COLLECTION = 'groupTimelineSavedViews';

export const GROUP_TIMELINE_LAST_VIEW_STORAGE_KEY = 'pm-hub-group-timeline-saved-view-id';

export interface GroupTimelineSavedView {
  id: string;
  name: string;
  ownerUid: string;
  ownerName: string;
  audienceUserIds: string[];
  audienceRoles: UserRole[];
  audienceDesignations: EmployeeDesignation[];
  family: GroupTimelineFamily;
  colorMode: GroupTimelineColorMode;
  layout: GroupTimelineLayoutMode;
  sort: GroupTimelineSortMode;
  filter: GroupTimelineDetailFilter;
  extraTasks: GroupTimelineExtraTasksByFamily;
  gantt: GanttGanttOutlineSlice;
  createdAt: Date | null;
  updatedAt: Date | null;
}

const USER_ROLES = new Set<string>([
  'employee',
  'employee_shop',
  'employee_office',
  'admin',
  'designer',
  'architect',
  'engineer',
  'staff',
  'supplier',
  'cpa',
  'subcontractor',
  'client',
]);

const DESIGNATIONS = new Set<string>([
  'project_manager',
  'estimator',
  'executive_assistant',
  'sales_rep',
  'cfo',
  'coo',
  'office_manager',
]);

function toDate(v: unknown): Date | null {
  const d = (v as { toDate?: () => Date } | undefined)?.toDate?.();
  return d instanceof Date && !Number.isNaN(d.getTime()) ? d : null;
}

function parseGanttSlice(raw: unknown): GanttGanttOutlineSlice {
  const row = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  const asArr = (v: unknown): string[] =>
    Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];
  return {
    collapsedGroups: asArr(row.collapsedGroups),
    collapsedSections: asArr(row.collapsedSections),
    expandedParentIds: asArr(row.expandedParentIds),
    pinKeys: asArr(row.pinKeys),
  };
}

function parseExtraTasks(raw: unknown): GroupTimelineExtraTasksByFamily {
  const out: GroupTimelineExtraTasksByFamily = {};
  if (!raw || typeof raw !== 'object') return out;
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    if (Array.isArray(v)) out[k as keyof GroupTimelineExtraTasksByFamily] = v.filter(x => typeof x === 'string');
  }
  return out;
}

export function parseGroupTimelineSavedView(id: string, data: Record<string, unknown>): GroupTimelineSavedView | null {
  const name = String(data.name ?? '').trim();
  const ownerUid = String(data.ownerUid ?? '').trim();
  if (!name || !ownerUid) return null;
  const roles = Array.isArray(data.audienceRoles)
    ? (data.audienceRoles.filter((r): r is UserRole => typeof r === 'string' && USER_ROLES.has(r)) as UserRole[])
    : [];
  const designations = Array.isArray(data.audienceDesignations)
    ? (data.audienceDesignations.filter(
        (d): d is EmployeeDesignation => typeof d === 'string' && DESIGNATIONS.has(d),
      ) as EmployeeDesignation[])
    : [];
  return {
    id,
    name,
    ownerUid,
    ownerName: String(data.ownerName ?? '').trim() || 'User',
    audienceUserIds: parseAudienceUserIds(data.audienceUserIds),
    audienceRoles: roles,
    audienceDesignations: designations,
    family: (data.family === 'all' ? 'all' : 'all') as GroupTimelineFamily,
    colorMode: (typeof data.colorMode === 'string' ? data.colorMode : 'project') as GroupTimelineColorMode,
    layout: (typeof data.layout === 'string' ? data.layout : 'project') as GroupTimelineLayoutMode,
    sort: (typeof data.sort === 'string' ? data.sort : 'supplier') as GroupTimelineSortMode,
    filter: parseGroupTimelineDetailFilter(data.filter),
    extraTasks: parseExtraTasks(data.extraTasks),
    gantt: parseGanttSlice(data.gantt),
    createdAt: toDate(data.createdAt),
    updatedAt: toDate(data.updatedAt),
  };
}

export function userCanAccessSavedView(
  view: Pick<
    GroupTimelineSavedView,
    'ownerUid' | 'audienceUserIds' | 'audienceRoles' | 'audienceDesignations'
  >,
  user: Pick<User, 'id' | 'role' | 'designations'> | null | undefined,
): boolean {
  if (!user?.id) return false;
  if (view.ownerUid === user.id) return true;
  return releaseMatchesUserAudience(
    {
      audienceUserIds: view.audienceUserIds,
      audienceRoles: view.audienceRoles,
      audienceDesignations: view.audienceDesignations,
    },
    user as User,
  );
}

export function buildSavedViewOutlineFromHubState(params: {
  family: GroupTimelineFamily;
  colorMode: GroupTimelineColorMode;
  layout: GroupTimelineLayoutMode;
  sort: GroupTimelineSortMode;
  filter: GroupTimelineDetailFilter;
  extraTasks: GroupTimelineExtraTasksByFamily;
  gantt: GanttGanttOutlineSlice;
}): Extract<GanttSyncedOutline, { mode: 'all' }> {
  return buildGanttSyncedOutlineAll(params);
}

export function applySavedViewToHub(
  view: GroupTimelineSavedView,
  handlers: ApplyGanttSyncedOutlineHandlers,
): void {
  const outline: Extract<GanttSyncedOutline, { mode: 'all' }> = {
    version: 1,
    mode: 'all',
    viewHash: '',
    family: 'all',
    colorMode: view.colorMode,
    layout: view.layout,
    sort: view.sort,
    filter: view.filter,
    extraTasks: view.extraTasks,
    gantt: view.gantt,
  };
  applyGanttSyncedOutline(outline, handlers);
}

export function storeLastAppliedSavedViewId(viewId: string | null): void {
  try {
    if (viewId) localStorage.setItem(GROUP_TIMELINE_LAST_VIEW_STORAGE_KEY, viewId);
    else localStorage.removeItem(GROUP_TIMELINE_LAST_VIEW_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

export function readLastAppliedSavedViewId(): string | null {
  try {
    return localStorage.getItem(GROUP_TIMELINE_LAST_VIEW_STORAGE_KEY);
  } catch {
    return null;
  }
}

/** Live list of views owned by or shared with the signed-in user. */
export function subscribeGroupTimelineSavedViewsForUser(
  user: Pick<User, 'id' | 'role' | 'designations'> | null | undefined,
  onData: (views: GroupTimelineSavedView[]) => void,
  onError?: (err: Error) => void,
): () => void {
  if (!user?.id) {
    onData([]);
    return () => {};
  }
  return onSnapshot(
    collection(db, GROUP_TIMELINE_SAVED_VIEWS_COLLECTION),
    snap => {
      const all: GroupTimelineSavedView[] = [];
      snap.docs.forEach(d => {
        const parsed = parseGroupTimelineSavedView(d.id, d.data() as Record<string, unknown>);
        if (parsed && userCanAccessSavedView(parsed, user)) all.push(parsed);
      });
      all.sort((a, b) => a.name.localeCompare(b.name));
      onData(all);
    },
    err => {
      console.warn('[groupTimelineSavedViews] subscribe', err);
      onError?.(err);
      onData([]);
    },
  );
}

export async function createGroupTimelineSavedView(input: {
  name: string;
  ownerUid: string;
  ownerName: string;
  outline: Extract<GanttSyncedOutline, { mode: 'all' }>;
  audienceUserIds?: string[];
  audienceRoles?: UserRole[];
  audienceDesignations?: EmployeeDesignation[];
}): Promise<string> {
  const ref = await addDoc(collection(db, GROUP_TIMELINE_SAVED_VIEWS_COLLECTION), {
    name: input.name.trim(),
    ownerUid: input.ownerUid,
    ownerName: input.ownerName,
    audienceUserIds: input.audienceUserIds ?? [],
    audienceRoles: input.audienceRoles ?? [],
    audienceDesignations: input.audienceDesignations ?? [],
    family: 'all',
    colorMode: input.outline.colorMode,
    layout: input.outline.layout,
    sort: input.outline.sort,
    filter: input.outline.filter,
    extraTasks: input.outline.extraTasks,
    gantt: input.outline.gantt,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return ref.id;
}

export async function updateGroupTimelineSavedView(
  viewId: string,
  patch: Partial<
    Pick<
      GroupTimelineSavedView,
      | 'name'
      | 'audienceUserIds'
      | 'audienceRoles'
      | 'audienceDesignations'
      | 'colorMode'
      | 'layout'
      | 'sort'
      | 'filter'
      | 'extraTasks'
      | 'gantt'
    >
  >,
): Promise<void> {
  const data: Record<string, unknown> = { updatedAt: serverTimestamp() };
  if (patch.name !== undefined) data.name = patch.name.trim();
  if (patch.audienceUserIds !== undefined) data.audienceUserIds = patch.audienceUserIds;
  if (patch.audienceRoles !== undefined) data.audienceRoles = patch.audienceRoles;
  if (patch.audienceDesignations !== undefined) data.audienceDesignations = patch.audienceDesignations;
  if (patch.colorMode !== undefined) data.colorMode = patch.colorMode;
  if (patch.layout !== undefined) data.layout = patch.layout;
  if (patch.sort !== undefined) data.sort = patch.sort;
  if (patch.filter !== undefined) data.filter = patch.filter;
  if (patch.extraTasks !== undefined) data.extraTasks = patch.extraTasks;
  if (patch.gantt !== undefined) data.gantt = patch.gantt;
  await updateDoc(doc(db, GROUP_TIMELINE_SAVED_VIEWS_COLLECTION, viewId), data as DocumentData);
}

export async function deleteGroupTimelineSavedView(viewId: string): Promise<void> {
  await deleteDoc(doc(db, GROUP_TIMELINE_SAVED_VIEWS_COLLECTION, viewId));
}
